import TableLoad from "components/load/TableLoad";
import PageError from "components/page-error";
import { PageTitle, PageLayout, ContentContainer } from "components/layout";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { toast } from "components/ui/use-toast";
import { useCallback } from "react";
import { useState } from "react";
import { Meta } from "../../../../@types/paginated";
import { useCampaignsData } from "./hooks/useCampaignsData";
import { handeOrderBy } from "../../../../utils/pagination";
import { useCampaignRouletteFilterActions } from "../ducks/filter/hooks/actions";
import { useFilterState } from "../ducks/filter/hooks/filterState";
import { columns } from "./columns";
import Table from "../components/table";
import { EmptyState } from "../components/empty-state";
import { Plus, RefreshCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import SelectFilters from "./components/select-filters";

export const CampaignRouletteList: React.FC = () => {
  const { updateFilter } = useCampaignRouletteFilterActions();
  const filterState = useFilterState();
  const { filters } = filterState || { filters: {} };
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const orderBy = handeOrderBy(filters.order || {});
  const orderByString = JSON.stringify(orderBy);

  const { campaigns, meta, isLoading, isFetching, error, refetch } = useCampaignsData({
    search: filters.search || "",
    status: filters.status || "",
    orderBy: orderByString,
    ...(filters.periodStart && { startDate: filters.periodStart }),
    ...(filters.periodEnd && { endDate: filters.periodEnd }),
    ...(filters.campaignType && { campaignType: filters.campaignType }),
    meta: {
      page: filters?.meta?.page || 1,
      perPage: filters?.meta?.perPage || 10,
    },
  });

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateFilter({ key: "search", value: event.target.value });
    },
    [updateFilter]
  );

  const load = isLoading;

  const handleCreateCampaign = () => {
    navigate("/crm/gestao-campanha-roleta/criar");
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await refetch().unwrap();
      toast({
        title: "Atualizado",
        description: "Os dados da listagem foram atualizados com sucesso.",
      });
    } catch (refreshError) {
      console.error("❌ Error refreshing campaign list:", refreshError);
      toast({
        title: "Erro ao atualizar",
        description: "Não foi possível atualizar a listagem. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  if (error) {
    return (
      <PageError
        status={500}
        message="Ocorreu um erro interno. Por favor, tente novamente mais tarde."
      />
    );
  }

  return (
    <PageLayout>
      <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <PageTitle
          title="Gestão de Campanha da Roleta"
          description="Gerencie campanhas da roleta para engajamento de clientes"
        />
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            className="h-8"
            onClick={handleRefresh}
            disabled={load || isRefreshing || isFetching}
          >
            <RefreshCcw className={`mr-2 h-4 w-4 ${(load || isRefreshing || isFetching) ? "animate-spin" : ""}`} />
            {load || isRefreshing || isFetching ? "Atualizando..." : "Atualizar"}
          </Button>
          <Button className="h-8" onClick={handleCreateCampaign}>
            <Plus className="h-4 w-4 mr-2" />
            Nova Campanha
          </Button>
        </div>
      </div>
      <ContentContainer>
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 xl:grid-cols-[minmax(0,320px)_1fr] xl:items-end">
            <div className="flex w-full flex-col gap-2">
              <Label htmlFor="buscar-campanhas" className="text-sm">
                Buscar
              </Label>
              <Input
                id="buscar-campanhas"
                placeholder="Buscar campanhas..."
                onChange={handleInputChange}
                className="h-8 w-full max-w-none xl:max-w-sm"
              />
            </div>
            <SelectFilters />
          </div>

            {load ? (
              <TableLoad />
            ) : campaigns.length > 0 ? (
              <Table
                data={campaigns}
                columns={columns}
                meta={meta as Meta}
                filters={filters}
                updateFilter={updateFilter}
              />
            ) : (
              <EmptyState
                variant="campaign"
                title="Nenhuma campanha encontrada"
                description="Não há campanhas cadastradas no momento."
                actionLabel="Nova Campanha"
                onAction={handleCreateCampaign}
                showAction={true}
              />
            )}
        </div>
      </ContentContainer>
    </PageLayout>
  );
};
